import type { AgentTint, PersonTint } from "@crew/ui/components/crew-avatar";
import type { FamilyShellAgent } from "@crew/ui/components/family-shell";
import type { Me } from "./me";

const AGENT_TINTS: AgentTint[] = ["ink", "sage", "peach"];
const PERSON_TINTS: PersonTint[] = ["sand", "sage", "rose"];

/** The API's tint strings, narrowed to the ones `@crew/ui` draws. */
export const agentTint = (tint: string): AgentTint =>
  AGENT_TINTS.includes(tint as AgentTint) ? (tint as AgentTint) : "ink";

export const personTint = (tint: string): PersonTint =>
  PERSON_TINTS.includes(tint as PersonTint) ? (tint as PersonTint) : "sand";

/**
 * A crew row in the sidebar: working while it has a running turn (slice 1
 * spec §3.3); "Waiting on you" in copper while a handoff of its waits or is
 * taken (slice 6c §7.1), which wins: someone is needed. The Chief of Staff
 * "Waits while you're on it" while the viewer holds the computer (§7.4).
 */
export function shellAgent(
  agent: Me["agents"][number],
  awake = false,
  holding = false,
): FamilyShellAgent {
  const working = agent.status === "working";
  const waiting = agent.waiting_on_you === true;
  const main = agent.slug === "main";
  return {
    id: agent.id,
    name: agent.name,
    glyph: agent.glyph,
    tint: agentTint(agent.tint),
    href: `/crew/${agent.slug}`,
    status: statusLine(waiting, holding && main, working),
    statusTone: waiting ? "copper" : working ? "moss" : undefined,
    // The computer's presence, on the Chief of Staff (`slug: main`) only.
    awake: awake && main,
  };
}

function statusLine(waiting: boolean, holding: boolean, working: boolean): string {
  if (waiting) return "Waiting on you";
  if (holding) return "Waits while you're on it";
  return working ? "Working" : "Ready to help";
}

/**
 * The side panel is on every shell page but Chats, which is the thread
 * surface already, and the Computer page, whose desktop takes the width
 * (slice 6c §7.4, boards 95–95c).
 */
export function hasPanel(pathname: string): boolean {
  const own = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  return !own("/chats") && !own("/computer");
}

/** "NB" for "Nadia Bell": a person's initials when the API didn't send them. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
